import React, { ReactNode } from 'react';
import { ExareStatus, MoneyFragment } from '@websky/graphql';
import { useTranslation } from 'react-i18next';
import { useHistory } from '@shared/routing';
import { Props as NotificationProps } from '../../../BaseComponents/Notification';
import { Money } from '../../../Money';

export const useExareInfoStatus = (
	type: 'refund' | 'exchange',
	exareStatus: ExareStatus,
	openTicket: () => void,
	passengersNames?: string,
	splittedOrderId?: string,
	price?: MoneyFragment
) => {
	const { t } = useTranslation(['Exchange', 'Refund', 'Checkout']);
	const history = useHistory();

	let title = `${t(type === 'refund' ? 'Request for refund' : 'Request for changes', {
		ns: type === 'refund' ? 'Refund' : 'Exchange'
	})}, ${t(exareStatus, { ns: type === 'exchange' ? 'Exchange' : 'Exchange' })}`;
	let status: NotificationProps['status'] = 'warning';
	let text: string | ReactNode = null;

	const actionText = !!splittedOrderId || exareStatus !== ExareStatus.Finished ? t('Open', { ns: 'Refund' }) : null;

	const onActionClick = () => {
		if (exareStatus === ExareStatus.Finished || (splittedOrderId && !exareStatus)) {
			history.push(`/booking/${splittedOrderId}`);
			window.location.reload();
		} else {
			openTicket();
		}
	};

	if (splittedOrderId && !exareStatus) {
		title = `${t('Order created from', { ns: 'Checkout' })} ${splittedOrderId}`;
		status = 'note';
	}

	switch (exareStatus) {
		case ExareStatus.Rejected:
		case ExareStatus.Refused:
		case ExareStatus.TimelimitExpired:
			status = 'error';
			break;

		case ExareStatus.NeedConfirm:
		case ExareStatus.InProcess:
		case ExareStatus.AwaitingPayment:
			status = 'warning';
			break;

		case ExareStatus.Finished:
			if (type === 'exchange') {
				title = [
					t('Successfully created new booking', {
						ns: 'Refund'
					}),
					splittedOrderId,
					passengersNames ? `${t('for').toLowerCase()} ${passengersNames}` : null
				]
					.filter(Boolean)
					.join(' ');
			} else {
				title = t('Reservation refund completed successfully', { ns: 'Refund' });

				text = (
					<>
						{t('Total amount', { ns: 'Refund' })}:{' '}
						<Money money={{ currency: price?.currency, amount: Math.abs(price?.amount) }} />
					</>
				);
			}

			status = 'done';
	}

	return {
		title,
		actionText,
		status,
		onActionClick,
		text
	};
};
